import { useCallback, useEffect, useReducer } from 'react';

import { SensitivityCategory } from '@ariksa/data-scanning';
import { SupportedServices } from '@ariksa/data-scanning/api';
import { each, map, orderBy, toArray, uniq } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { publishedAtOptions } from 'components/DataEntry';
import { IconTypes } from 'components/Icons';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useCloudAccounts } from 'containers/App/hooks/useCloudAccounts';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions as sharedStateActions } from 'containers/SharedState/slice';
import { getDataSourceIcon } from 'containers/Visibility/Data/Components/hooks/utils';
import { cloudOptions } from 'containers/Visibility/Data/Components/utils';
import { selectDataDashboard } from 'containers/Visibility/Data/selectors';

import { actions } from '../../slice';

export const useDataDashboard = () => {
  const {
    dataSources,
    dataSecurityIdentities,
    dataTypeSensitiveSources,
    sensitiveSourcesByDataType,
    selectedTab,
    dataClassifiers,
  } = useSelector(selectDataDashboard);
  const { regions, searchTerm } = useSelector(selectSharedState);
  const dispatch = useDispatch();
  const { accountId, environmentId } = useAccessBoundary();
  const { getResourceAlias } = useResourceType();
  const { getAccountOptions } = useCloudAccounts();
  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      cloudOptions: cloudOptions,
      accountOptions: [],
      sensitivityOptions: map(SensitivityCategory, o => ({
        label: o,
        value: o,
      })),
      regionOptions: [],
      sourceTypeOptions: [],
      resourceTypeOptions: [{}],
      dataTypeOptions: [],
      labelOptions: [],
      selectedCloud: {},
      selectedRegion: {},
      selectedAccount: {},
      selectedLabel: {},
      selectedSourceType: {},
      selectedResourceType: {},
      selectedSensitivity: {},
      selectedDataType: {},
      time: publishedAtOptions[0],
      showDashboard: false,
      resourceType: {},
    },
  );
  const [drawerState, updateDrawerState] = useReducer(
    (prev, next) => ({ ...prev, ...next }),
    {
      accountOptions: [],
      selectedCloud: {},
      selectedRegion: {},
      selectedAccount: {},
      selectedLabel: {},
      selectedSourceType: {},
      selectedResourceType: {},
      selectedSensitivity: {},
      selectedDataType: {},
      time: publishedAtOptions[0],
    },
  );
  const {
    time: dashboardTime,
    selectedRegion: dashboardSelectedRegion,
    selectedSourceType: dashboardSelectedSourceType,
    selectedCloud: dashboardSelectedCloud,
    selectedAccount: dashboardSelectedAccount,
    selectedLabel: dashboardSelectedLabel,
  } = state;
  const {
    time: drawerTime,
    selectedRegion: drawerSelectedRegion,
    selectedSourceType: drawerSelectedSourceType,
    selectedCloud: drawerSelectedCloud,
    selectedAccount: drawerSelectedAccount,
    selectedLabel: drawerSelectedLabel,
  } = drawerState;

  //set region options
  useEffect(() => {
    updateState({
      regionOptions: map(regions.data, o => ({ label: o, value: o })),
    });
  }, [regions.data]);

  //set data type options
  useEffect(() => {
    let options: Record<string, any>[] = [];
    let labels = new Set();
    each(dataClassifiers.data, o => {
      each(o?.labels, l => labels.add(l));
      options.push({ label: o?.read_name, value: o?.name });
    });
    updateState({
      dataTypeOptions: options,
      labelOptions: orderBy(
        map(toArray(labels), o => ({ label: o, value: o })),
        ['label'],
        'asc',
      ),
    });
  }, [dataClassifiers.data]);

  useEffect(() => {
    const resourceTypes = uniq(
      map(dataSecurityIdentities.data, identity =>
        identity?.native_resource_type ? identity.native_resource_type : null,
      ).filter(Boolean),
    );
    updateState({
      resourceTypeOptions: resourceTypes.map(resourceType => ({
        label: getResourceAlias(resourceType),
        value: resourceType,
      })),
    });
  }, [dataSecurityIdentities.data, getResourceAlias]);

  //account options
  useEffect(() => {
    updateState({
      accountOptions: getAccountOptions(state?.selectedCloud?.key),
    });
  }, [getAccountOptions, state?.selectedCloud]);

  // Keep drawer account options independent from the main dashboard filters.
  useEffect(() => {
    updateDrawerState({
      accountOptions: getAccountOptions(drawerState?.selectedCloud?.key),
    });
  }, [drawerState?.selectedCloud, getAccountOptions]);

  const getDataSourceAlias = useCallback(
    type => {
      switch (type) {
        case SupportedServices.GitLabBranch:
          return 'GitLab';
        case SupportedServices.GitHubBranch:
          return 'GitHub';
        case SupportedServices.BitBucketBranch:
          return 'BitBucket';
        default:
          return getResourceAlias(type);
      }
    },
    [getResourceAlias],
  );

  //set source type options
  useEffect(() => {
    let options: Record<string, any>[] = [];
    each(SupportedServices, o =>
      options.push({
        label: getDataSourceAlias(o),
        value: o,
        icon: getDataSourceIcon(o),
      }),
    );
    updateState({ sourceTypeOptions: options, selectedSourceType: options[0] });
  }, [getDataSourceAlias]);

  const getDataSources = useCallback(() => {
    if (!environmentId && !accountId) return;
    const payload = {
      accountIds:
        !!accountId || !!state.selectedAccount?.value
          ? [state.selectedAccount?.value ?? accountId]
          : [],
      environmentId,
      page: dataSources.page.info.page_number,
      size: dataSources.page.info.page_size,
      days: state.time?.value,
    };
    dispatch(
      actions.getDataSources({
        q: {
          ...payload,
          region: state.selectedRegion?.value,
          sourceType: state.selectedSourceType?.value,
          cloud: state.selectedCloud?.value?.toLowerCase(),
          label: state.selectedLabel?.value,
          dataType: state.selectedDataType?.value,
          searchTerm,
        },
        onSuccess: res => {
          !!res?.total &&
            dispatch(
              sharedStateActions.getRiskContext({
                q: {
                  riskContextRequest: {
                    uuids: map(res?.items, o => o?.id!),
                  },
                },
              }),
            );
        },
      }),
    );
  }, [
    dispatch,
    accountId,
    environmentId,
    dataSources.page.info,
    state.time,
    state.selectedRegion,
    state.selectedSourceType,
    state.selectedCloud,
    state.selectedLabel,
    state.selectedAccount,
    state.selectedDataType,
    searchTerm,
  ]);

  const getDataTypes = useCallback(() => {
    if (!environmentId && !accountId) return;
    const payload = {
      accountIds: !!accountId ? [accountId] : undefined,
      environmentId,
      page: dataSources.page.info.page_number,
      size: dataSources.page.info.page_size,
      days: state.time?.value,
    };
    dispatch(
      actions.getDataTypes({
        q: {
          ...payload,
          cloud: state.selectedCloud?.value,
          label: state.selectedLabel?.value,
          sensitivity: state.selectedSensitivity?.value,
          searchTerm,
        },
        onSuccess: res => {
          !!res?.total &&
            dispatch(
              actions.getRiskContextForDataTypes({
                q: {
                  accountId: !!accountId ? [accountId] : [],
                  environmentId: environmentId!,
                  dataType: map(res?.items, o => o.sensitive_data_name),
                },
              }),
            );
        },
      }),
    );
  }, [
    dispatch,
    accountId,
    environmentId,
    dataSources.page.info,
    state.time,
    state.selectedCloud,
    state.selectedLabel,
    state.selectedSensitivity,
    searchTerm,
  ]);

  const getDocumentTypes = useCallback(() => {
    if (!environmentId && !accountId) return;
    const payload = {
      accountIds: !!accountId ? [accountId] : [],
      environmentId,
      page: dataSources.page.info.page_number,
      size: dataSources.page.info.page_size,
      days: state.time?.value,
    };
    dispatch(
      actions.getDocumentTypes({
        q: {
          ...payload,
          cloud: state.selectedCloud?.value,
          label: state.selectedLabel?.value,
          searchTerm,
        },
        onSuccess: res => {
          !!res?.total &&
            dispatch(
              actions.getRiskContextForDataTypes({
                q: {
                  accountId: !!accountId ? [accountId] : [],
                  environmentId: environmentId!,
                  dataType: map(res?.items, o => o.type_id),
                },
              }),
            );
        },
      }),
    );
  }, [
    dispatch,
    accountId,
    environmentId,
    dataSources.page.info,
    state.time,
    state.selectedCloud,
    state.selectedLabel,
    searchTerm,
  ]);

  const getDataFootprint = useCallback(() => {
    if (!environmentId && !accountId) return;
    const payload = {
      accountIds: !!accountId ? [accountId] : undefined,
      environmentId: environmentId as string,
    };
    dispatch(
      actions.getPubliclyExposedDataSources({
        q: {
          environmentId,
          accountId: !!accountId ? [accountId] : undefined,
        },
      }),
    );
    dispatch(
      actions.getSensitiveDataSourceCount({
        q: payload,
      }),
    );
    dispatch(
      actions.getAlertingCount({
        q: { ...payload, forDataSources: true },
      }),
    );
  }, [dispatch, accountId, environmentId]);

  const getDataTypeSources = useCallback(
    (dataType, useDrawerState = false) => {
      const filters = useDrawerState
        ? {
            time: drawerTime,
            selectedRegion: drawerSelectedRegion,
            selectedSourceType: drawerSelectedSourceType,
            selectedCloud: drawerSelectedCloud,
            selectedAccount: drawerSelectedAccount,
            selectedLabel: drawerSelectedLabel,
          }
        : {
            time: dashboardTime,
            selectedRegion: dashboardSelectedRegion,
            selectedSourceType: dashboardSelectedSourceType,
            selectedCloud: dashboardSelectedCloud,
            selectedAccount: dashboardSelectedAccount,
            selectedLabel: dashboardSelectedLabel,
          };
      const pageInfo = useDrawerState
        ? sensitiveSourcesByDataType.page.info
        : dataTypeSensitiveSources.page.info;
      const payload = {
        accountIds:
          !!accountId || !!filters.selectedAccount?.value
            ? [filters.selectedAccount?.value ?? accountId]
            : [],
        environmentId,
        page: pageInfo.page_number,
        size: pageInfo.page_size,
        days: filters.time?.value,
      };
      dispatch(
        (useDrawerState
          ? actions.getSensitiveSourcesByDataType
          : actions.getDataTypeSources)({
          q: {
            ...payload,
            region: filters.selectedRegion?.value,
            sourceType: filters.selectedSourceType?.value,
            cloud: filters.selectedCloud?.value?.toLowerCase(),
            label: filters.selectedLabel?.value,
            dataType,
            searchTerm,
          },
          onSuccess: res => {
            !!res?.total &&
              dispatch(
                sharedStateActions.getRiskContext({
                  q: {
                    riskContextRequest: {
                      uuids: map(res?.items, o => o?.id!),
                    },
                  },
                }),
              );
          },
        }),
      );
    },
    [
      dispatch,
      accountId,
      environmentId,
      dataTypeSensitiveSources.page.info,
      sensitiveSourcesByDataType.page.info,
      dashboardTime,
      dashboardSelectedRegion,
      dashboardSelectedSourceType,
      dashboardSelectedCloud,
      dashboardSelectedAccount,
      dashboardSelectedLabel,
      drawerTime,
      drawerSelectedRegion,
      drawerSelectedSourceType,
      drawerSelectedCloud,
      drawerSelectedAccount,
      drawerSelectedLabel,
      searchTerm,
    ],
  );

  const getAllDataResourceTypes = useCallback(() => {
    dispatch(
      actions.getAllDataResourceTypes({
        q: {
          accountIds: !!accountId ? [accountId] : [],
          environmentId,
        },
      }),
    );
  }, [dispatch, accountId, environmentId]);

  const getDataSecurityIdentities = useCallback(() => {
    if (!environmentId && !accountId) return;
    dispatch(
      actions.getDataSecurityIdentities({
        q: {
          accountId: accountId ? [accountId] : undefined,
          environmentId,
          page: dataSecurityIdentities.page.info.page_number,
          size: dataSecurityIdentities.page.info.page_size,
          cloud: state.selectedCloud?.value,
          resourceType: state.selectedResourceType?.value,
          label: state.selectedLabel?.value,
          dataType: state.selectedDataType?.value,
          search: searchTerm,
        },
        onSuccess: res => {
          if (res?.total) {
            dispatch(
              sharedStateActions.getRiskContext({
                q: {
                  riskContextRequest: {
                    uuids: map(res?.items, identity => identity?.uuid!),
                  },
                },
              }),
            );
          }
        },
      }),
    );
  }, [
    dispatch,
    accountId,
    environmentId,
    dataSecurityIdentities.page.info,
    state.selectedCloud,
    state.selectedResourceType,
    state.selectedLabel,
    state.selectedDataType,
    searchTerm,
  ]);

  const updateValue = useCallback((field, value) => {
    updateState({ [field]: value });
  }, []);

  const updateDrawerValue = useCallback((field, value) => {
    updateDrawerState({ [field]: value });
  }, []);

  const getTableData = useCallback(() => {
    selectedTab === 'Identities'
      ? getDataSecurityIdentities()
      : selectedTab === IconTypes.DataSources
      ? getDataSources()
      : selectedTab === 'DataTypes'
      ? getDataTypes()
      : selectedTab === 'all'
      ? getAllDataResourceTypes()
      : getDocumentTypes();
  }, [
    selectedTab,
    getDataSources,
    getDataTypes,
    getDocumentTypes,
    getAllDataResourceTypes,
    getDataSecurityIdentities,
  ]);

  return {
    getDataSources,
    getTableData,
    time: state.time,
    cloudOptions: state.cloudOptions,
    accountOptions: state.accountOptions,
    regionOptions: state.regionOptions,
    labelOptions: state.labelOptions,
    sourceTypeOptions: state.sourceTypeOptions,
    resourceTypeOptions: state.resourceTypeOptions,
    selectedRegion: state.selectedRegion,
    selectedAccount: state.selectedAccount,
    selectedCloud: state.selectedCloud,
    selectedLabel: state.selectedLabel,
    selectedSourceType: state.selectedSourceType,
    selectedResourceType: state.selectedResourceType,
    sensitivityOptions: state.sensitivityOptions,
    selectedSensitivity: state.selectedSensitivity,
    showDashboard: state.showDashboard,
    dataTypeOptions: state.dataTypeOptions,
    selectedDataType: state.selectedDataType,
    drawerFilters: {
      time: drawerState.time,
      regionOptions: state.regionOptions,
      accountOptions: drawerState.accountOptions,
      sourceTypeOptions: state.sourceTypeOptions,
      resourceTypeOptions: state.resourceTypeOptions,
      selectedSourceType: drawerState.selectedSourceType,
      selectedResourceType: drawerState.selectedResourceType,
      selectedAccount: drawerState.selectedAccount,
      selectedRegion: drawerState.selectedRegion,
      labelOptions: state.labelOptions,
      cloudOptions: state.cloudOptions,
      selectedCloud: drawerState.selectedCloud,
      selectedLabel: drawerState.selectedLabel,
      selectedSensitivity: drawerState.selectedSensitivity,
      selectedDataType: drawerState.selectedDataType,
      sensitivityOptions: state.sensitivityOptions,
      dataTypeOptions: state.dataTypeOptions,
    },
    updateValue,
    updateDrawerValue,
    getDataFootprint,
    getDataTypes,
    getDataTypeSources,
    getDataSecurityIdentities,
  };
};
